import { powi } from './dmath.ts';
import { AMPLIFY, GRAVITY, KINDS, KIND_STATS, L, MARK, PITBOSS, RAMP, RANK_DAMAGE, RUNNER, STORM, SURGE, TIME_STOP, TOTEM_STATS, VAMPIRE_CHARGE, hordeHp, masteryPower, type Kind } from './config.ts';
import { DT, enemyById, targetable, type Ctx } from './context.ts';
import { aimY, routeLength, spawn } from './horde.ts';
import { random, range } from './rng.ts';
import { isBoss, type Attack, type BossClass, type Cell, type Enemy } from './state.ts';

const dist = (ax: number, az: number, bx: number, bz: number) => Math.sqrt((ax - bx) * (ax - bx) + (az - bz) * (az - bz));

/* ---------------- damage and death ---------------- */

export function damage(ctx: Ctx, e: Enemy, dmg: number, kind: Kind, hitFx: boolean, owner: number, extraCharge = 0) {
  if (!e.alive) return;
  if (e.mark > 0) dmg *= MARK.power;
  e.hp -= dmg;
  if (hitFx) {
    e.knock = isBoss(e.cls) ? 0.12 : e.cls === 'runner' ? 0.32 : 0.5;
    ctx.ev.push({ type: 'hit', id: e.id, kind });
  }
  if (e.hp <= 0) { die(ctx, e, kind, owner, extraCharge); return; }
  // half and quarter health: armour cracks off, so a boss visibly wears down
  if (isBoss(e.cls) && e.stage < 2 && e.hp / e.maxHp < (e.stage ? 0.25 : 0.5)) {
    e.stage++;
    e.knock = 0.6;
    ctx.ev.push({ type: 'stagger', id: e.id });
  }
}

function die(ctx: Ctx, e: Enemy, kind: Kind, killer: number, extraCharge: number) {
  const { st } = ctx;
  e.alive = false;
  if (st.stats.kills[killer] !== undefined) { st.stats.kills[killer]++; if (isBoss(e.cls)) st.stats.bossKills[killer]++; }
  if (kind === 's' && st.players[killer]) st.players[killer].storm++;
  // a bot seat never spends, so its souls would only pile up at its cap: they fly to the first human seal
  let owner = killer;
  if (st.players[owner]?.bot) { const human = st.players.findIndex((p) => !p.bot); if (human >= 0) owner = human; }
  const boss = isBoss(e.cls);
  // the soul leaves the body and flies home to charge the killer's seal (a bot's: a human's); a boss releases three
  const value = (e.cls === 'pitboss' ? PITBOSS.reward : boss ? 6 : 1) + (kind === 'v' ? extraCharge : 0), count = boss ? 3 : 1;
  const souls: { delay: number; dur: number; value: number }[] = [];
  for (let k = 0; k < count; k++) {
    const soul = { owner, t: -k * 0.12, dur: range(st.rng, 1.15, 1.4) + k * 0.05, value: value / count, boss, blood: kind === 'v' };
    st.souls.push(soul);
    souls.push({ delay: k * 0.12, dur: soul.dur, value: soul.value });
  }
  ctx.ev.push({ type: 'death', id: e.id, kind, cls: e.cls, owner, souls });
}

/* ---------------- waves ---------------- */

export function spawnBoss(ctx: Ctx, cls: BossClass, x?: number) {
  const pit = cls === 'pitboss';
  spawn(ctx, cls, { lane: cls === 'brute' ? 2 : 1, s: x !== undefined ? x - L.spawnX : 0, hp: (pit ? PITBOSS.hp : 900) * hordeHp(ctx.st.clock, 'elite', ctx.st.mode) });
}

/** A column already standing in the courtyard, with gaps for the three elites (a test fixture: matches start empty). */
export function openingColumn(ctx: Ctx) {
  const bossX = [-0.5, -3.05, -1.8];
  for (let lane = 0; lane < 4; lane++) {
    for (let x = L.fogEndX - 0.2 + lane * 0.13; x < L.gateX - 1.7 - lane * 0.04; x += range(ctx.st.rng, 0.36, 0.46)) {
      if (bossX.some((bx) => Math.abs(bx - x) < 0.6)) continue;
      spawn(ctx, 'soldier', { lane, s: x - (L.spawnX - lane * 0.13) });
    }
  }
  spawnBoss(ctx, 'brute', bossX[0]);
  spawnBoss(ctx, 'herald', bossX[1]);
  spawnBoss(ctx, 'pitboss', bossX[2]);
}

/** One soldier at a time out of the fog, the gaps closing over the first minutes; elites on timers. */
function waves(ctx: Ctx, dt: number) {
  const { st } = ctx, R = st.rng;
  st.nextSpawn -= dt;
  if (st.nextSpawn <= 0 && st.enemies.length < 90) {
    // the crowd thickens with time (k); each soldier hardens by the balance's curve
    const k = 1 + st.clock / 90;
    spawn(ctx, 'soldier', { hp: 60 * hordeHp(st.clock, 'soldier', st.mode) });
    const calm = Math.max(0, 1 - st.clock / RAMP.rampEnd);
    st.nextSpawn = range(R, 0.55, 1.0) / Math.min(2, Math.sqrt(k)) * (1 + (RAMP.calm - 1) * calm * calm);
  }
  st.nextBoss -= dt;
  if (st.nextBoss <= 0) { spawnBoss(ctx, st.bossToggle++ % 2 ? 'herald' : 'brute'); st.nextBoss = 40; }
  st.nextRunner -= dt;
  if (st.nextRunner <= 0) {
    let alive = 0;
    for (const e of st.enemies) if (e.alive && e.cls === 'runner') alive++;
    if (alive < RUNNER.maxAlive && st.enemies.length < 90) {
      spawn(ctx, 'runner', { hp: RUNNER.hp * hordeHp(st.clock, 'elite', st.mode) });
      st.nextRunner = range(R, RUNNER.minInterval, RUNNER.maxInterval);
    } else st.nextRunner = 1;
  }
  // a surge: a pack out of the fog at once, bigger with time, now and then with runners
  st.nextSurge -= dt;
  if (st.nextSurge <= 0 && st.enemies.length < 80) {
    const n = Math.floor(SURGE.base + st.clock / SURGE.grow + random(R) * SURGE.spread);
    for (let i = 0; i < n; i++) spawn(ctx, 'soldier', { s: random(R) * 0.8, hp: 60 * hordeHp(st.clock, 'soldier', st.mode) });
    const runners = random(R) < SURGE.runners;
    if (runners) for (let i = 0; i < 2; i++) spawn(ctx, 'runner', { s: random(R) * 0.5, hp: RUNNER.hp * hordeHp(st.clock, 'elite', st.mode) });
    ctx.ev.push({ type: 'surge', size: n + (runners ? 2 : 0), runners });
    st.nextSurge = range(R, SURGE.every[0], SURGE.every[1]);
  }
  st.nextPitboss -= dt;
  if (st.nextPitboss <= 0) {
    let alive = 0;
    for (const e of st.enemies) if (e.alive && e.cls === 'pitboss') alive++;
    if (alive < PITBOSS.maxAlive && st.enemies.length < 90) {
      spawnBoss(ctx, 'pitboss');
      st.nextPitboss = range(R, PITBOSS.minInterval, PITBOSS.maxInterval);
    } else st.nextPitboss = 2;
  }
}

/* ---------------- relics ---------------- */

/** Attack-rate multiplier: the player's totem and modifiers, plus adjacent citrines feeding embers and frosts. */
export function relicRate(ctx: Ctx, player: number, index: number): number {
  const p = ctx.st.players[player], cell = p.cells[index];
  // a legendary ember takes what a buff adds threefold (AMPLIFY): the totem's rate, citrine's link
  const amp = AMPLIFY[cell.kind]?.buff ?? 1;
  const base = p.rate * (1 + (TOTEM_STATS[p.totem].rate - 1) * amp);
  if (cell.kind !== 'r' && cell.kind !== 'b') return base;
  let bonus = 0;
  for (const link of ctx.layout.altars[player].links) {
    const o = link.a === index ? link.b : link.b === index ? link.a : -1;
    const n = o >= 0 ? p.cells[o] : null;
    if (n && n.kind === 'y' && n.phase !== 'empty' && n.phase !== 'incoming') bonus += 0.15 + n.level * 0.05;
  }
  return (1 + bonus * amp) * base;
}

/** Wind-ups and channels of a relic end the moment it changes (merge, ascension). */
export function touchCell(ctx: Ctx, player: number, index: number) {
  const cell = ctx.st.players[player].cells[index];
  cell.version++;
  if (cell.castTarget >= 0) { cell.castTarget = -1; cell.castLeft = 0; ctx.ev.push({ type: 'cancel', player, cell: index }); }
}

function gem(ctx: Ctx, player: number, index: number) {
  const a = ctx.layout.altars[player], c = a.cells[index];
  return { x: c.x, y: a.gemY, z: c.z };
}

export function pickTarget(ctx: Ctx, player: number, index: number, kind: Kind): Enemy | null {
  if (kind === 't') {
    const g = gem(ctx, player, index);
    let nearest: Enemy | null = null, distance = Infinity;
    for (const e of ctx.st.enemies) {
      if (!targetable(e)) continue;
      const d = (e.crowdX - g.x) * (e.crowdX - g.x) + (e.crowdZ - g.z) * (e.crowdZ - g.z);
      if (d < distance) { distance = d; nearest = e; }
    }
    return nearest;
  }
  let best: Enemy | null = null, bp = -1;
  for (const e of ctx.st.enemies) {
    if (!targetable(e)) continue;
    // sun bolts and venom lobs favour bosses a little; everyone else takes the front-runner
    const w = e.s / routeLength(ctx, e) + (isBoss(e.cls) && (kind === 'y' || kind === 'g') ? 0.15 : 0);
    if (w > bp) { bp = w; best = e; }
  }
  return best;
}

/** Wind-up belongs to the interval, so distinct animations keep the same DPS. */
function fire(ctx: Ctx, player: number, index: number, cell: Cell, dt: number) {
  const p = ctx.st.players[player];
  const rate = relicRate(ctx, player, index) * (p.hasteT > 0 ? 1 + (AMPLIFY[cell.kind]?.haste ?? 1) : 1) * masteryPower(p.mastery[KINDS.indexOf(cell.kind)]);
  let c = cell.cd - dt * rate;
  if (cell.castTarget >= 0) {
    cell.castLeft -= dt * rate;
    if (cell.castLeft <= 0) {
      const locked = enemyById(ctx, cell.castTarget);
      const target = targetable(locked) ? locked : pickTarget(ctx, player, index, cell.kind);
      cell.castTarget = -1; cell.castLeft = 0;
      if (target) shoot(ctx, player, index, cell, target);
      else ctx.ev.push({ type: 'cancel', player, cell: index });
    }
  } else if (c <= 0) {
    const target = pickTarget(ctx, player, index, cell.kind);
    if (target) {
      const st = KIND_STATS[cell.kind];
      cell.castTarget = target.id; cell.castLeft = st.windup;
      ctx.ev.push({ type: 'prepare', player, cell: index, kind: cell.kind, level: cell.level, target: target.id, windup: st.windup / rate });
      c = st.interval * (1 - Math.min(0.4, (cell.level - 1) * 0.06));
    } else c = 0.1;
  }
  cell.cd = c;
}

export function shoot(ctx: Ctx, player: number, index: number, cell: Cell, e: Enemy) {
  const { st } = ctx, p = st.players[player];
  const kind = cell.kind, level = cell.level, stats = KIND_STATS[kind];
  const from = gem(ctx, player, index);
  // the totem's might (threefold on a legendary ember) and the sphere's mastery
  // mastery: the blow, the pace (fire) and the sphere's signature effect each grow by its step a level
  const mastery = masteryPower(p.mastery[KINDS.indexOf(kind)]);
  const power = p.power * (1 + (TOTEM_STATS[p.totem].power - 1) * (AMPLIFY[kind]?.buff ?? 1)) * mastery;
  const dmg = stats.damage * RANK_DAMAGE[level - 1] * power;
  const aim = { x: e.crowdX, y: aimY(e), z: e.crowdZ };
  const shot = { type: 'shoot' as const, player, cell: index, kind, level, target: e.id, aimX: aim.x, aimY: aim.y, aimZ: aim.z, dur: 0, chain: null as number[] | null };
  if (kind === 'v') {
    // one immediate bite; no splash or shared skillshot
    ctx.ev.push(shot);
    damage(ctx, e, dmg, 'v', true, player, VAMPIRE_CHARGE[level - 1] * mastery);
    return;
  }
  if (kind === 'k' || kind === 's') {
    // obsidian's and the storm eye's bolts strike at once (a bolt in flight would be lost on a target the
    // others finish first — a third of them, and the mark or the charge with it); the flight is drawn only
    const dx = aim.x - from.x, dy = aim.y - from.y, dz = aim.z - from.z;
    shot.dur = Math.min(0.15, Math.sqrt(dx * dx + dy * dy + dz * dz) / stats.projectileSpeed);
    ctx.ev.push(shot);
    if (kind === 'k') {
      // the mark lands first: the bolt that sets it already hits harder
      const duration = (MARK.duration + MARK.perLevel * level) * mastery;
      e.mark = Math.max(e.mark, duration);
      ctx.ev.push({ type: 'mark', player, id: e.id, level, duration });
      damage(ctx, e, dmg, 'k', true, player);
    } else {
      ctx.ev.push({ type: 'storm_hit', player, id: e.id, level, charges: p.storm });
      damage(ctx, e, dmg * (1 + STORM.perKill * p.storm), 's', true, player);
    }
    return;
  }
  if (kind === 'o') {
    // a gravity well opens a little ahead of the front-runner, where the crowd will walk into it
    const g = level - 1, x = e.crowdX + e.tangentX * 0.35, z = e.crowdZ + e.tangentZ * 0.35;
    if (st.vortices.length >= 10) st.vortices.shift();
    const dur = GRAVITY.duration[g] * mastery;
    st.vortices.push({ owner: player, x, z, radius: GRAVITY.radius[g], time: 0, dur, level, dps: dmg });
    ctx.ev.push(shot);
    ctx.ev.push({ type: 'well', player, cell: index, level, x, z, radius: GRAVITY.radius[g], dur });
    return;
  }
  if (kind === 't') {
    const g = level - 1, targets = [e], radius = TIME_STOP.radius[g];
    // small, nearest-first group around the primary victim, bounded to four enemies at V
    while (targets.length < TIME_STOP.targets[g]) {
      let next: Enemy | null = null, distance = radius * radius;
      for (const other of st.enemies) {
        if (!targetable(other) || targets.includes(other)) continue;
        const d = (other.crowdX - e.crowdX) * (other.crowdX - e.crowdX) + (other.crowdZ - e.crowdZ) * (other.crowdZ - e.crowdZ);
        if (d <= distance) { distance = d; next = other; }
      }
      if (!next) break;
      targets.push(next);
    }
    ctx.ev.push(shot);
    for (const target of targets) {
      if (target.timeStop <= 0) target.stasisAttack = target.attacking;
      target.timeStop = Math.max(target.timeStop, TIME_STOP.duration[g] * mastery);
      target.knock = 0;
      ctx.ev.push({ type: 'time_lock', id: target.id, duration: TIME_STOP.duration[g] * mastery });
      // the stop strikes as it holds
      if (dmg > 0) damage(ctx, target, dmg, 't', true, player);
    }
    return;
  }
  if (kind === 'g') {
    const aimX = e.crowdX + e.speed * 0.25, aimZ = e.crowdZ;
    // a runner sees the spore coming and side-steps; the lob may land beside it
    if (e.cls === 'runner' && random(st.rng) < 0.6) e.dodge = (random(st.rng) < 0.5 ? -1 : 1) * range(st.rng, 1.4, 2);
    const dx = aimX - from.x, dy = 0.12 - from.y, dz = aimZ - from.z;
    const dur = 0.55 + Math.sqrt(dx * dx + dy * dy + dz * dz) * 0.04;
    st.lobs.push({ owner: player, level, power, target: e.id, aimX, aimZ, dmg, t: 0, dur });
    ctx.ev.push({ ...shot, aimX, aimY: 0.12, aimZ, dur });
    return;
  }
  const a: Attack = { kind: kind as Attack['kind'], owner: player, cell: index, version: cell.version, target: e.id, level, atX: aim.x, atY: aim.y, atZ: aim.z, time: 0, damage: dmg, flight: 0 };
  if (kind === 'b') {
    a.atX = e.crowdX + e.speed * 0.18; a.atY = 0.035; a.atZ = e.crowdZ;
    shot.aimX = a.atX; shot.aimY = a.atY; shot.aimZ = a.atZ;
  } else if (kind === 'a') {
    // an arcane missile: a fast homing bolt that strikes when it arrives
    const dx = aim.x - from.x, dy = aim.y - from.y, dz = aim.z - from.z;
    a.flight = Math.min(0.3, 0.06 + Math.sqrt(dx * dx + dy * dy + dz * dz) / stats.projectileSpeed);
    shot.dur = a.flight;
  } else if (kind === 'y') {
    const chain = [e];
    let last = e;
    for (let k = 0; k < Math.min(4, level); k++) {
      let nearest: Enemy | null = null, distance = 1.35;
      for (const other of st.enemies) {
        if (!targetable(other) || chain.includes(other)) continue;
        const d = dist(other.crowdX, other.crowdZ, last.crowdX, last.crowdZ);
        if (d < distance) { distance = d; nearest = other; }
      }
      if (!nearest) break;
      chain.push(nearest); last = nearest;
    }
    shot.chain = chain.map((x) => x.id);
    ctx.ev.push(shot);
    chain.forEach((other, k) => damage(ctx, other, dmg * powi(0.7, k), 'y', true, player));
    // the arc lingers on screen, but its damage is already done
    return;
  }
  ctx.ev.push(shot);
  if (st.attacks.length < 80) st.attacks.push(a);
}

/* ---------------- the fixed step ---------------- */

/** Gravity wells drag everyone inside toward the centre, slow them and burn a little; bosses resist by their mass. */
function gravity(ctx: Ctx, dt: number) {
  const list = ctx.st.vortices;
  for (let i = list.length - 1; i >= 0; i--) {
    const v = list[i]; v.time += dt;
    const g = v.level - 1, ramp = Math.min(1, v.time / 0.25);
    for (const e of ctx.st.enemies) {
      if (!targetable(e) || e.timeStop > 0) continue;
      const dx = v.x - e.crowdX, dz = v.z - e.crowdZ, d = Math.sqrt(dx * dx + dz * dz);
      if (d > v.radius + e.radius) continue;
      const k = Math.min(d, GRAVITY.pull[g] * ramp * dt / Math.sqrt(e.mass)) / Math.max(d, 1e-3);
      e.s = Math.max(0, e.s + (dx * e.tangentX + dz * e.tangentZ) * k);
      e.side += (-dx * e.tangentZ + dz * e.tangentX) * k;
      e.slow = Math.max(e.slow, GRAVITY.slow[g]); e.slowT = Math.max(e.slowT, 0.25);
      damage(ctx, e, v.dps * dt, 'o', false, v.owner);
    }
    if (v.time >= v.dur) list.splice(i, 1);
  }
}

export function stepCombatBefore(ctx: Ctx) {
  const dt = DT;
  waves(ctx, dt);
  gravity(ctx, dt);
}

export function stepCombatAfter(ctx: Ctx) {
  const { st } = ctx, dt = DT;
  // poison ticks
  for (const e of st.enemies) {
    if (!e.alive || e.poisonT <= 0) continue;
    e.poisonT = Math.max(0, e.poisonT - dt); e.poison = Math.min(1, e.poisonT);
    damage(ctx, e, e.poisonDps * dt, 'g', false, e.poisonOwner);
    if (e.poisonT === 0) e.poisonDps = 0;
  }

  st.players.forEach((p, pi) => p.cells.forEach((cell, ci) => {
    if (cell.phase === 'idle' && cell.kind !== 'w') fire(ctx, pi, ci, cell, dt);
  }));

  // venom lobs
  for (let i = st.lobs.length - 1; i >= 0; i--) {
    const l = st.lobs[i];
    l.t += dt;
    if (l.t < l.dur) continue;
    st.lobs.splice(i, 1);
    const radius = 0.7 + l.level * 0.08, dps = 6 * l.level * l.power;
    if (st.venom.length >= 32) st.venom.shift();
    st.venom.push({ owner: l.owner, x: l.aimX, z: l.aimZ, radius, time: 3, dps });
    ctx.ev.push({ type: 'lob_impact', player: l.owner, level: l.level, x: l.aimX, z: l.aimZ, radius });
    for (const e of st.enemies) {
      if (!e.alive || dist(e.crowdX, e.crowdZ, l.aimX, l.aimZ) >= radius) continue;
      e.poisonT = 3; e.poisonDps = dps; e.poison = 1; e.poisonOwner = l.owner;
      damage(ctx, e, l.dmg, 'g', e.id === l.target, l.owner);
    }
  }

  // channels, ground eruptions and missiles never share a flying skillshot
  for (let i = st.attacks.length - 1; i >= 0; i--) {
    const a = st.attacks[i];
    const cell = st.players[a.owner].cells[a.cell];
    if (cell.version !== a.version || cell.phase !== 'idle') { st.attacks.splice(i, 1); continue; }
    const previous = a.time; a.time += dt;
    if (a.kind === 'r') {
      const from = gem(ctx, a.owner, a.cell), target = enemyById(ctx, a.target);
      if (target && target.alive) { a.atX = target.crowdX; a.atY = aimY(target); a.atZ = target.crowdZ; }
      const dx = a.atX - from.x, dz = a.atZ - from.z, len = Math.sqrt(dx * dx + dz * dz), nx = dx / Math.max(len, 0.01), nz = dz / Math.max(len, 0.01);
      const slice = Math.min(dt, Math.max(0, 0.46 - previous));
      for (const e of st.enemies) {
        if (!targetable(e)) continue;
        const ex = e.crowdX - from.x, ez = e.crowdZ - from.z, along = ex * nx + ez * nz, across = Math.abs(ex * nz - ez * nx);
        if (along < 0 || along > len + 0.35 || across > 0.12 + along / Math.max(len, 0.1) * (0.4 + 0.04 * a.level)) continue;
        damage(ctx, e, a.damage * slice / 0.46 * (e.id === a.target ? 1 : 0.38), 'r', previous === 0, a.owner);
      }
      if (a.time >= 0.46) st.attacks.splice(i, 1);
    } else if (a.kind === 'a') {
      if (a.time < a.flight) continue;
      const target = enemyById(ctx, a.target);
      if (targetable(target)) {
        ctx.ev.push({ type: 'arcane_hit', player: a.owner, id: target.id, level: a.level });
        damage(ctx, target, a.damage, 'a', true, a.owner);
      }
      st.attacks.splice(i, 1);
    } else if (a.kind === 'b') {
      if (a.time < 0.18) continue;
      ctx.ev.push({ type: 'eruption', player: a.owner, level: a.level, x: a.atX, z: a.atZ });
      const radius = 0.55 + 0.065 * a.level;
      for (const e of st.enemies) if (targetable(e) && dist(e.crowdX, e.crowdZ, a.atX, a.atZ) < radius + e.radius) {
        e.slow = Math.min(1, 0.5 + a.level * 0.08); e.slowT = (1.6 + a.level * 0.2) * masteryPower(st.players[a.owner].mastery[KINDS.indexOf('b')]);
        damage(ctx, e, a.damage, 'b', true, a.owner);
      }
      st.attacks.splice(i, 1);
    } else st.attacks.splice(i, 1);
  }

  // venom pools keep poisoning whoever wades through
  for (let i = st.venom.length - 1; i >= 0; i--) {
    const v = st.venom[i]; v.time -= dt;
    for (const e of st.enemies) if (targetable(e) && dist(e.crowdX, e.crowdZ, v.x, v.z) < v.radius) {
      e.poisonT = Math.max(e.poisonT, 0.8);
      if (v.dps >= e.poisonDps) { e.poisonDps = v.dps; e.poisonOwner = v.owner; }
      e.poison = 1;
    }
    if (v.time <= 0) st.venom.splice(i, 1);
  }

  // souls home to their seal
  for (let i = st.souls.length - 1; i >= 0; i--) {
    const s = st.souls[i];
    s.t += dt;
    if (s.t < s.dur) continue;
    st.souls.splice(i, 1);
    const p = st.players[s.owner];
    if (!p) continue;
    p.sp += s.value;
    ctx.ev.push({ type: 'soul_arrive', player: s.owner, value: s.value, boss: s.boss, blood: s.blood });
  }
}
